// ============================================================================
// apps/web/src/pages/DashboardPage.tsx
// ----------------------------------------------------------------------------
// Page d'accueil : tuiles vers les modules (filtrées par capacités), bannière
// « Mon profil », vues par PV et relevés de température. La liste des tuiles
// provient du registre partagé nav-modules.ts (même source que la sidebar) :
// pour ajouter/retirer un module, modifier CE registre, pas cette page.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ChevronRight, HeartPulse, MapPin, Plus, User } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { Capability } from '@magic/shared';
import { supabase } from '@/lib/supabase';
import { HOME_TILES } from '@/lib/nav-modules';
import { useNewCandidaturesCount } from '@/lib/use-candidatures-count';
import { useMyOpenTasksCount } from '@/lib/use-my-tasks-count';
import { useNewNewsletterCount } from '@/lib/use-newsletter-count';
import { useNewPortageCount } from '@/lib/use-portage-count';
import { useNewCommandesCount } from '@/lib/use-commandes-count';
import { usePendingAbsencesCount } from '@/lib/use-pending-absences-count';
import { useNewFeedbackCount } from '@/lib/use-feedback-count';

type QuickLocation = { id: string; name: string; slug: string; type: string };

// Les tuiles de l'accueil proviennent du registre partagé (source de vérité
// unique, commune avec la sidebar). Cf. apps/web/src/lib/nav-modules.ts.
const TILES = HOME_TILES;


export default function DashboardPage() {
  const { me, hasCapability } = useAuth();
  const greeting = me?.profile.first_name ?? me?.profile.email ?? '';

  // ─── Chargement des PV pour les "Saisies rapides par PV" ─────────────
  const [locations, setLocations] = useState<QuickLocation[]>([]);
  // v0.32 — Profil "Info urgence" incomplet ? On affiche une bannière jaune.
  // Critère : au moins UN champ vide parmi (date_of_birth, emergency_contact_phone,
  // medical_treatment, address). 4 champs vides à la création → bannière forte.
  const [profileMissing, setProfileMissing] = useState<string[] | null>(null);

  useEffect(() => {
    if (!me?.profile.id) return;
    void (async () => {
      const [locsRes, profRes] = await Promise.all([
        supabase.from('locations')
          .select('id, name, slug, type')
          .eq('is_active', true)
          .order('display_order'),
        supabase.from('user_profiles')
          .select('date_of_birth, emergency_contact_phone, medical_treatment, address')
          .eq('id', me.profile.id)
          .single(),
      ]);
      setLocations((locsRes.data as QuickLocation[]) ?? []);

      // Détecte les champs manquants pour la bannière
      const p = profRes.data as {
        date_of_birth: string | null;
        emergency_contact_phone: string | null;
        medical_treatment: string | null;
        address: string | null;
      } | null;
      if (p) {
        const missing: string[] = [];
        if (!p.date_of_birth)           missing.push('date de naissance');
        if (!p.emergency_contact_phone) missing.push('contact d\'urgence');
        if (!p.address)                 missing.push('adresse');
        // medical_treatment optionnel — pas tout le monde a un traitement
        setProfileMissing(missing.length > 0 ? missing : null);
      }
    })();
  }, [me?.profile.id]);

  // ─── PV de l'utilisateur (admin voit tout, autres voient leurs PV) ────
  const isAdminLike = hasCapability(Capability.ADMIN_ALL)
                   || hasCapability(Capability.PLANNING_READ_ALL);
  const myLocationIds = useMemo(
    () => new Set((me?.locations ?? []).map((ul) => ul.location_id)),
    [me?.locations],
  );
  const myLocations = useMemo(
    () => isAdminLike ? locations : locations.filter((l: QuickLocation) => myLocationIds.has(l.id)),
    [isAdminLike, locations, myLocationIds],
  );

  const canFeedbackWrite = hasCapability(Capability.FEEDBACK_WRITE);
  // Badge « nouvelles candidatures » (statut 'nouveau') sur la tuile Candidatures.
  const newCandidatures = useNewCandidaturesCount();
  // Badge « nouvelles tâches » (to-do MM, nominatif, non vues) sur la tuile To-Do.
  const { count: myTasks, markSeen: markTasksSeen } = useMyOpenTasksCount();
  // Badge « inscriptions newsletter non traitées » (status 'new') sur la tuile Newsletter.
  const newNewsletters = useNewNewsletterCount();
  // Badge « demandes de portage non traitées » (statut 'nouveau') sur la tuile Portage.
  const newPortage = useNewPortageCount();
  // Badge « commandes cuisine reçues » (statut 'en_attente') sur la tuile Production.
  const newCommandes = useNewCommandesCount();
  const newFeedback = useNewFeedbackCount();
  // Badge « demandes de congé en attente » (status 'pending') sur la tuile Planning.
  const newAbsences = usePendingAbsencesCount();

  return (
    <div className="container py-4 sm:py-8 space-y-6 sm:space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Bonjour {greeting}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Magic — Plateforme d'exploitation Le Pitchoun &amp; Nulle Pâte Ailleurs
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canFeedbackWrite && (
            <Link
              to="/feedback/new"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 sm:px-5 sm:py-3 text-sm font-medium shadow-md hover:bg-primary/90 hover:shadow-lg active:scale-95 transition-all"
              title="Saisie rapide (retour produit, tâche, température, étiquette)"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden sm:inline">Saisie rapide</span>
            </Link>
          )}
        </div>
      </div>

      {/* ─── Bannière Mon profil ─────────────────────────────────────────
          Affichage adapté à l'état de complétude des infos perso :
            - profil incomplet → bannière jaune avec liste des champs manquants
            - profil complet   → carte verte discrète "à jour"
          Dans les deux cas, mène vers /me/profile. */}
      {profileMissing && profileMissing.length > 0 ? (
        <Link to="/me/profile" className="block">
          <Card className="border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-amber-200 p-2 shrink-0">
                <HeartPulse className="h-5 w-5 text-amber-800" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900">
                  Complète tes infos d'urgence
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Manquant : {profileMissing.join(', ')}. Utile au secouriste
                  en cas de pépin au travail.
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-amber-700 shrink-0" />
            </CardContent>
          </Card>
        </Link>
      ) : profileMissing !== null ? (
        // profileMissing = [] (chargé, rien manquant) → carte verte discrète
        <Link to="/me/profile" className="block">
          <Card className="border-emerald-200 bg-emerald-50/50 hover:bg-emerald-100/50 transition-colors">
            <CardContent className="p-3 flex items-center gap-3">
              <User className="h-4 w-4 text-emerald-700 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-semibold text-emerald-900">Mon profil</span>
                <span className="text-emerald-800 ml-2">— mes infos sont à jour</span>
              </div>
              <ChevronRight className="h-4 w-4 text-emerald-700 shrink-0" />
            </CardContent>
          </Card>
        </Link>
      ) : null}

      {/* ─── Mes points de vente ──────────────────────────────────────── */}
      {myLocations.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <h2 className="text-base sm:text-lg font-semibold">
              {isAdminLike ? 'Vues par point de vente' : 'Mon point de vente'}
              {myLocations.length > 1 && !isAdminLike ? 's' : ''}
            </h2>
          </div>
          <p className="mb-3 text-xs sm:text-sm text-muted-foreground">
            Appuie sur un PV → planning de la semaine, hygiène, documents, accès rapides.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3">
            {myLocations.map((l) => (
              <Link key={l.id} to={`/pv/${l.slug}`} className="block h-full">
                <Card className="h-full transition-shadow hover:shadow-md hover:border-primary/40 active:scale-[0.98]">
                  <CardContent className="p-3 flex items-center gap-2">
                    <MapPin className="h-5 w-5 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{l.name}</div>
                      <div className="text-[11px] text-muted-foreground">Vue du PV</div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ─── Modules ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TILES.filter((t) => !t.capability || hasCapability(t.capability as never)).map((t) => {
          const Icon = t.icon;
          const title = t.title ?? t.label;
          const badge = t.key === 'candidatures' ? newCandidatures
            : t.key === 'todo' ? myTasks
            : t.key === 'newsletter' ? newNewsletters
            : t.key === 'portage' ? newPortage
            : t.key === 'production' ? newCommandes
            : t.key === 'planning' ? newAbsences
            : t.key === 'feedback' ? newFeedback
            : 0;

          const inner = (
            <Card className="relative h-full transition-shadow hover:shadow-md">
              {badge > 0 && (
                <span className="absolute right-3 top-3 z-10 rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white shadow">
                  {badge > 99 ? '99+' : badge} nouvelle{badge > 1 ? 's' : ''}
                </span>
              )}
              <CardHeader>
                <div className="flex items-start gap-3">
                  <Icon className="h-8 w-8 shrink-0 text-primary" />
                  <div>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription className="mt-1">{t.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          );

          if (t.href) {
            return (
              <a key={t.key} href={t.href} target="_blank" rel="noreferrer" className="block h-full" onClick={() => { if (t.key === 'todo') markTasksSeen(); }}>
                {inner}
              </a>
            );
          }
          return (
            <Link key={t.key} to={t.to!} className="block h-full">
              {inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
